import { SpeakingSlotDto, SpeakingSlotStatus, SpeakingSlotType } from 'api/planning';
import { SpeakingSlotAction } from './SpeakingSlotAction';
import { getSpeakingSlotStatusFromSpeakingSlotAction } from './getSpeakingSlotStatusFromSpeakingSlotAction';
import { useEffect, useMemo, useState } from 'react';
import { formatTimeOnly } from 'utils';
import { RoomClashPeriodDto } from './utils/calculateClashedPeriods';
import { normalizeEndTime } from './utils/normalizeEndTime';
import { getEndTimeAfterStart } from './utils/getEndTimeAfterStart';
import { NUMBER_OF_SLOTS, SLOT_TIME_MINUTES } from './speakingSlotTime';
import { getSlotCapacityForStatus } from './utils/getSlotCapacityForStatus';
import { getSpeakingStructureTimeBounds } from './utils/getSpeakingStructureTimeBounds';

type Props = {
  localSpeakingSlotMinutesOffset: number;
  slots: SpeakingSlotDto[];
  occupiedSlots: SpeakingSlotDto[];
  roomSwaSlots: SpeakingSlotDto[];
  clashedPeriods: RoomClashPeriodDto[];
  editing: boolean;
  selectedSlotAction: SpeakingSlotAction;
  speakingSlotType: SpeakingSlotType;
  plannedCapacity: number;
  onChangeSlots: (value: SpeakingSlotDto[]) => void;
};

export const useAllocationGrid = ({
  editing,
  localSpeakingSlotMinutesOffset,
  slots,
  occupiedSlots,
  roomSwaSlots,
  clashedPeriods,
  selectedSlotAction,
  speakingSlotType,
  plannedCapacity,
  onChangeSlots,
}: Props) => {
  const isAnySwaSeatBooked = (slot?: { plannedCapacity?: number; availableCapacity?: number }) =>
    speakingSlotType === SpeakingSlotType.SWA &&
    (slot?.plannedCapacity ?? 0) > (slot?.availableCapacity ?? 0);

  const [lastHoveredSlot, setLastHoveredSlot] = useState<string>();
  const [selectionStartSlot, setSelectionStartSlot] = useState<string>();
  const [selecting, setSelecting] = useState(false);

  const emptyGrid = useMemo(() => {
    return Array.from({ length: NUMBER_OF_SLOTS }, (_, i) => {
      const totalStartMinutes =
        (i * SLOT_TIME_MINUTES + localSpeakingSlotMinutesOffset) % (24 * 60);
      const totalEndMinutes = totalStartMinutes + SLOT_TIME_MINUTES;

      const startHour = Math.floor(totalStartMinutes / 60);
      const startMinutes = totalStartMinutes % 60;
      const endHour = Math.floor(totalEndMinutes / 60) % 24;
      const endMinutes = totalEndMinutes % 60;

      return {
        startTime: formatTimeOnly(startHour, startMinutes),
        endTime: formatTimeOnly(endHour, endMinutes),
        startMinutes: String(startMinutes).padStart(2, '0'),
        endMinutes: String(endMinutes).padStart(2, '0'),
      };
    });
  }, [localSpeakingSlotMinutesOffset]);

  const grid = emptyGrid.map((x) => {
    const matchingSlot = slots.find((s) => s.startTime === x.startTime && s.endTime === x.endTime);
    const isSwaBufferSlot =
      !matchingSlot &&
      (roomSwaSlots.some((s) => s.startTime === x.endTime || s.endTime === x.startTime) ||
        (speakingSlotType === SpeakingSlotType.SWA &&
          occupiedSlots.some((s) => s.startTime === x.endTime || s.endTime === x.startTime)));

    return Object.assign(x, {
      status: matchingSlot?.status,
      plannedCapacity: matchingSlot?.plannedCapacity,
      availableCapacity: matchingSlot?.availableCapacity,
      occupied:
        occupiedSlots.some((s) => s.startTime === x.startTime && s.endTime === x.endTime) ||
        isSwaBufferSlot,
      clashed: clashedPeriods.some(
        (c) =>
          c.startTime <= x.startTime &&
          getEndTimeAfterStart(x.startTime, x.endTime) <= normalizeEndTime(c.endTime)
      ),
      confirmed: true,
      booking: matchingSlot?.booking,
    });
  });

  const onMouseEnterSlot = (startTime: string) => {
    const slot = grid.find((x) => x.startTime === startTime);
    setLastHoveredSlot(startTime);

    if (
      selecting &&
      selectedSlotAction !== SpeakingSlotAction.AddBreak &&
      slot?.occupied === true
    ) {
      setSelecting(false);
    }

    const slotStatus = slot?.status;

    if (selecting && slot?.clashed && selectedSlotAction !== SpeakingSlotAction.Clear) {
      setSelecting(false);
    }

    if (
      selecting &&
      (slotStatus === SpeakingSlotStatus.Booked || isAnySwaSeatBooked(slot)) &&
      selectedSlotAction === SpeakingSlotAction.Clear
    ) {
      setSelecting(false);
    }

    if (
      selecting &&
      slotStatus === SpeakingSlotStatus.Rejected &&
      selectedSlotAction !== SpeakingSlotAction.Clear &&
      selectedSlotAction !== SpeakingSlotAction.AddSlot
    ) {
      setSelecting(false);
    }
  };

  const canSelectSlot = (slot: string | undefined): boolean => {
    if (!slot || !grid) return false;

    const isEmptyStructure = grid.findIndex((x) => x?.status !== undefined) === -1;

    const slotIndex = grid.findIndex((x) => x.startTime === slot);
    const selectedSlot = grid[slotIndex];
    if (!selectedSlot) return false;

    if (selectedSlot.status === SpeakingSlotStatus.Booked || isAnySwaSeatBooked(selectedSlot)) {
      return false;
    }

    if (selectedSlot.clashed === true && selectedSlotAction !== SpeakingSlotAction.Clear) {
      return false;
    }

    if (selectedSlot.status === undefined && selectedSlotAction === SpeakingSlotAction.Clear) {
      return false;
    }

    if (
      selectedSlotAction === SpeakingSlotAction.Clear &&
      selectedSlot.status === SpeakingSlotStatus.Break
    ) {
      return true;
    }

    if (selectedSlot.occupied === true) {
      return false;
    }

    if (
      selectedSlotAction === SpeakingSlotAction.AddBreak &&
      speakingSlotType === SpeakingSlotType.RSS
    ) {
      return false;
    }

    if (
      selectedSlot.status === SpeakingSlotStatus.Rejected &&
      selectedSlotAction !== SpeakingSlotAction.Clear &&
      selectedSlotAction !== SpeakingSlotAction.AddSlot
    ) {
      return false;
    }

    const nextSlot = grid[slotIndex + 1];
    const previousSlot = grid[slotIndex - 1];

    if (selectedSlotAction !== SpeakingSlotAction.Clear) {
      return (
        isEmptyStructure || nextSlot?.status !== undefined || previousSlot?.status !== undefined
      );
    }

    return nextSlot?.status === undefined || previousSlot?.status === undefined;
  };

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!canSelectSlot(lastHoveredSlot)) {
      return;
    }
    setSelecting(true);
    setSelectionStartSlot(lastHoveredSlot);
  };

  const onMouseLeave = () => {
    setLastHoveredSlot(undefined);
  };

  const applyActionToSlot = (slot: (typeof grid)[number]) => {
    const status = getSpeakingSlotStatusFromSpeakingSlotAction(
      selectedSlotAction,
      speakingSlotType
    );

    if (status === undefined) {
      return { ...slot, status, confirmed: false };
    }

    const capacity = getSlotCapacityForStatus(speakingSlotType, status, plannedCapacity);
    return {
      ...slot,
      status,
      confirmed: false,
      plannedCapacity: capacity,
      availableCapacity: capacity,
    };
  };

  const enrichedGrid = useMemo(() => {
    if (!editing || !selecting || !selectionStartSlot || !lastHoveredSlot) return grid;

    const isSingleSwaSlotSelection =
      speakingSlotType === SpeakingSlotType.SWA &&
      selectedSlotAction === SpeakingSlotAction.AddSlot;

    const startTimeNumber = convertTimeToNumber(selectionStartSlot);
    const endTimeNumber = convertTimeToNumber(lastHoveredSlot);
    const selection = [startTimeNumber, endTimeNumber].sort((a, b) => a - b);

    const tempGrid = grid.map((x) => {
      const startTime = convertTimeToNumber(x.startTime);
      if (
        (isSingleSwaSlotSelection
          ? x.startTime === selectionStartSlot
          : selection[0] <= startTime && selection[1] >= startTime) &&
        x.status !== SpeakingSlotStatus.Booked &&
        !isAnySwaSeatBooked(x) &&
        (x.clashed === false || selectedSlotAction === SpeakingSlotAction.Clear) &&
        (selectedSlotAction === SpeakingSlotAction.Clear || !x.occupied) &&
        !(
          speakingSlotType === SpeakingSlotType.RSS &&
          selectedSlotAction === SpeakingSlotAction.AddBreak
        )
      ) {
        return applyActionToSlot(x);
      }
      return x;
    });

    const isTimelineContinuous =
      tempGrid.reduce((acc, curr) => {
        if (acc > 2) return acc;
        if (curr.status !== undefined && acc % 2 === 0) return acc + 1;
        if (curr.status === undefined && acc % 2 === 1) return acc + 1;

        return acc;
      }, 0) < 3;

    return isTimelineContinuous ? tempGrid.map((x) => ({ ...x, confirmed: true })) : tempGrid;
  }, [
    grid,
    lastHoveredSlot,
    selectedSlotAction,
    editing,
    selecting,
    selectionStartSlot,
    speakingSlotType,
    plannedCapacity,
  ]);

  useEffect(() => {
    const onMouseUp = () => {
      if (selecting) {
        if (lastHoveredSlot) {
          const isTimelineContinuous = !enrichedGrid.some((x) => !x.confirmed);

          if (isTimelineContinuous) {
            onChangeSlots(
              enrichedGrid
                .filter((x) => x.status !== undefined)
                .map((x) => ({
                  status: x.status!,
                  startTime: x.startTime,
                  endTime: x.endTime,
                  plannedCapacity: x.plannedCapacity ?? 0,
                  availableCapacity: x.availableCapacity ?? 0,
                }))
            );
          }
        }
        setSelecting(false);
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (selecting && e.key == 'Escape') {
        setSelecting(false);
      }
    };

    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [selecting, lastHoveredSlot, onChangeSlots, enrichedGrid]);

  const structureTimeBounds = getSpeakingStructureTimeBounds(slots);

  return {
    grid: enrichedGrid,
    onMouseEnterSlot,
    onMouseDown,
    onMouseLeave,
    selecting,
    canSelectSlot,
    structureStartTime: structureTimeBounds.startTime,
    structureEndTime: structureTimeBounds.endTime,
  };
};

const convertTimeToNumber = (time: string) => {
  const timeParts = time.split(':');
  return +(timeParts[0] + String(timeParts[1]).padStart(2, '0'));
};
